import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Tooltip,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { MainLayout } from '../components/MainLayout';
import { investmentsService, type ACBPosition, type InvestmentTrade } from '../services/investments-service';
import { accountsService } from '../services/accounts-service';
import type { Account } from '../types/api';
import { AccountPicker, defaultAccountFor, type OwnerScope } from '../components/AccountPicker';
import { useAuthStore } from '../stores/auth-store';
import { httpClient } from '../services/http-client';
import { SymbolReviewBanner } from '../components/SymbolReviewBanner';
import { BookValueEditor } from '../components/BookValueEditor';
import { formatCurrency as fmtMoney, formatShares } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';
import { SymbolLedgerDialog } from '../components/SymbolLedgerDialog';
import { PositionMix } from '../components/PositionMix';

export const HoldingsPage: React.FC = () => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [holdings, setHoldings] = useState<ACBPosition[]>([]);
  // The trade history, not lots. `/lots` is the POOLED position since
  // migration 054 -- one row per symbol at one average cost -- so per-purchase
  // detail, fees and trade-date FX come from `/trades`.
  const [lots, setLots] = useState<InvestmentTrade[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState<string>('');
  const [tabValue, setTabValue] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [loadingLots, setLoadingLots] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Add transaction dialog state
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [transactionForm, setTransactionForm] = useState({
    symbol: '',
    shares: '',
    price: '',
    date: new Date().toISOString().split('T')[0],
    transaction_type: 'BUY' as 'BUY' | 'SELL',
    // Commission belongs IN the cost base on a buy and REDUCES proceeds on a
    // sell. Leaving it out overstates the reported capital gain, so it is a
    // first-class field rather than something buried in the notes.
    fees: '',
    // Rate to CAD ON THE TRADE DATE, not today. Blank means par, which is
    // correct for a CAD trade.
    fx_rate: '',
    notes: '',
  });

  // Sorting for Holdings tab
  type HoldingsSortField = 'symbol' | 'total_shares' | 'total_cost_basis' | 'acb_per_share';
  type SortDirection = 'asc' | 'desc';
  const currentUserId = useAuthStore((st) => st.user?.id);
  const baseCurrency = useBaseCurrency();
  /**
   * Selection survives a reload.
   *
   * Per USER, not globally: two members share a browser profile in this
   * household, and restoring one person's account for the other is the same
   * confusion the picker exists to remove.
   */
  const selectionKey = `holdings:selection:${currentUserId ?? 'anon'}`;
  const [scope, setScope] = useState<OwnerScope>(() => {
    try { return (localStorage.getItem(`${selectionKey}:scope`) as OwnerScope) || 'mine'; }
    catch { return 'mine'; }
  });
  /** The brokerage's own snapshot, shown when no trade history exists. */
  const [institutionHoldings, setInstitutionHoldings] = useState<any[]>([]);
  /** Symbol -> the brokerage's own market value, in the base currency. */
  const [institutionBySymbol, setInstitutionBySymbol] =
    useState<Record<string, number>>({});


  /** Symbol whose purchase history is open, or null. */
  const [ledgerSymbol, setLedgerSymbol] = useState<string | null>(null);
  // Live prices, keyed by symbol. The ACB table knows what a holding COST;
  // market value needs a quote, which lives on the portfolio endpoint.
  const [quotes, setQuotes] = useState<Record<string, { price: number; value: number }>>({});
  /**
   * Every priced position, per ACCOUNT.
   *
   * The quotes map above is keyed by symbol across the whole household, which
   * is fine for a price and wrong for a position: XEQT is held in two accounts
   * and its pooled 210 shares are not this account's 110. The chart needs the
   * per-account figure or it values the account above its own balance and
   * reports zero cash.
   */
  const [portfolioRows, setPortfolioRows] = useState<any[]>([]);

  // Live quotes for the market-value column. Top-level, NOT inside a handler:
  // a hook called conditionally never runs and breaks React's ordering.
  useEffect(() => {
    let cancelled = false;
    httpClient
      .get<any>('/portfolio')
      .then((r) => {
        if (cancelled) return;
        const hs = ((r as any)?.data?.holdings ?? (r as any)?.holdings ?? []) as any[];
        setPortfolioRows(hs);
        const map: Record<string, { price: number; value: number }> = {};
        hs.forEach((h) => {
          if (h.has_price) {
            map[String(h.symbol).toUpperCase()] = {
              price: Number(h.price) || 0,
              value: Number(h.market_value) || 0,
            };
          }
        });
        setQuotes(map);
      })
      .catch((err) => {
        // Was silently swallowed, which is how this failed invisibly: no
        // quotes, no chart, and dashes where market value should be, with
        // nothing in the console to say why. Book cost still renders -- a
        // missing quote is not fatal -- but it must be SAYABLE.
        if (!cancelled) {
          // eslint-disable-next-line no-console
          console.warn('[holdings] portfolio quotes unavailable', err);
          setQuotes({});
          setPortfolioRows([]);
        }
      });
    return () => { cancelled = true; };
    // Re-runs when the selected account changes. Previously [] -- it fired
    // ONCE on mount, so a request that lost the race with authentication
    // returned 401 and was never retried for the life of the page.
  }, [selectedAccountId]);
  const [holdingsSortField, setHoldingsSortField] = useState<HoldingsSortField>('symbol');
  const [holdingsSortDirection, setHoldingsSortDirection] = useState<SortDirection>('asc');

  // Sorting for Lots tab
  type LotsSortField = 'trade_date' | 'symbol' | 'transaction_type' | 'shares' | 'price' | 'amount';
  const [lotsSortField, setLotsSortField] = useState<LotsSortField>('trade_date');
  const [lotsSortDirection, setLotsSortDirection] = useState<SortDirection>('desc');

  const fetchAccounts = useCallback(async () => {
    try {
      const response = await accountsService.getAccountsWithBalances();
      const accountsData = response?.data || [];
      const investmentAccounts = accountsData.filter(
        (acc: Account) => acc.account_type === 'Investment'
      );
      setAccounts(investmentAccounts);
      if (investmentAccounts.length > 0 && !selectedAccountId) {
        // A remembered account only wins if it is still in the list -- an
        // account that has since been closed or unlinked must not leave the
        // page stuck on an id that resolves to nothing.
        // An account named in the URL WINS over the remembered one.
        //
        // Arriving from the accounts list is an explicit request for a
        // specific account; restoring the last-viewed one instead silently
        // showed a different account's holdings than the one clicked, which
        // reads as "my positions vanished" rather than "you are on the wrong
        // account".
        const requested = (() => {
          try {
            return new URLSearchParams(window.location.search).get('account');
          } catch { return null; }
        })();
        if (requested && investmentAccounts.some((a: any) => a.id === requested)) {
          setSelectedAccountId(requested);
          return;
        }

        const remembered = (() => {
          try { return localStorage.getItem(`${selectionKey}:account`); } catch { return null; }
        })();
        if (remembered && investmentAccounts.some((a: any) => a.id === remembered)) {
          setSelectedAccountId(remembered);
          return;
        }
        // Land on the member's OWN account. Defaulting to whatever sorted
        // first put a signed-in member on their partner's account, where an
        // empty page reads as "I have no holdings" rather than "this is not
        // your account".
        setSelectedAccountId(defaultAccountFor(investmentAccounts as any, currentUserId) ?? '');
      }
    } catch (err: any) {
      console.error('Failed to fetch accounts:', err);
      setError('Failed to load accounts');
    }
  }, [selectedAccountId]);

  const fetchHoldings = useCallback(async () => {
    if (!selectedAccountId) return;

    setLoading(true);
    setError(null);
    try {
      const response = await investmentsService.getACBSummary(selectedAccountId);
      const summary = response?.data || (response as any);
      let holdingsData = summary.positions || [];

      // Always fetch what the BROKERAGE reports, and show the positions ACB
      // cannot account for.
      //
      // An account can have both: a few symbols with real trade history and
      // many with only a book cost from the institution. Falling back only when
      // ACB was entirely empty hid the second group -- this account holds 14
      // positions, 3 have trades, and 12 real holdings worth six figures simply
      // did not render. "Some history" was worse than none.
      try {
        const inst = await httpClient.get<any[]>(
          `/investments/accounts/${selectedAccountId}/institution-holdings`);
        const all = Array.isArray(inst) ? inst : [];
        // ACB wins wherever it exists: it is replayed from trades and accounts
        // for dispositions and superficial losses, which a reported book cost
        // cannot. The institution fills the gaps.
        // EVERY position the brokerage reports, not just the ones ACB cannot
        // account for.
        //
        // The table below renders either this list or the ACB one, never both,
        // so filtering it to the ACB-less remainder meant an account with 9
        // holdings listed 3 -- the six with trade history vanished from the
        // table while still appearing in the chart above it.
        //
        // Cost basis here is the brokerage's own figure, which the banner
        // above the table says plainly. Per-symbol ACB stays available on the
        // symbol drilldown, where the replayed history it depends on lives.
        setInstitutionHoldings(all);
        // The FULL list is kept for valuation. The filtered list above is what
        // the institution TABLE renders -- the gaps ACB cannot account for --
        // but the brokerage's market value is the best figure available for
        // every position, including the ones ACB does cover.
        setInstitutionBySymbol(
          Object.fromEntries(
            all.map((h: any) => [String(h.symbol).toUpperCase(),
              Number(h.value_base) || Number(h.value) || 0]),
          ),
        );
      } catch {
        setInstitutionHoldings([]);
        setInstitutionBySymbol({});
      }
      
      // Apply sorting
      holdingsData = [...holdingsData].sort((a: ACBPosition, b: ACBPosition) => {
        let aValue: any;
        let bValue: any;
        
        switch (holdingsSortField) {
          case 'symbol':
            aValue = (a.symbol || '').toLowerCase();
            bValue = (b.symbol || '').toLowerCase();
            break;
          case 'total_shares':
            aValue = parseFloat(a.total_shares || '0');
            bValue = parseFloat(b.total_shares || '0');
            break;
          case 'total_cost_basis':
            aValue = parseFloat(a.total_cost_basis || '0');
            bValue = parseFloat(b.total_cost_basis || '0');
            break;
          case 'acb_per_share':
            aValue = parseFloat(a.acb_per_share || '0');
            bValue = parseFloat(b.acb_per_share || '0');
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return holdingsSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return holdingsSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      setHoldings(holdingsData);
    } catch (err: any) {
      console.error('Error fetching holdings:', err);
      // Extract error message from ApiError or generic Error
      let errorMessage = 'Failed to fetch holdings';
      if (err && typeof err === 'object') {
        if (err.message) {
          errorMessage = err.message;
        } else if (err.error) {
          errorMessage = err.error;
        } else if (err.details?.message) {
          errorMessage = err.details.message;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      setHoldings([]);
    } finally {
      setLoading(false);
    }
  }, [selectedAccountId, holdingsSortField, holdingsSortDirection]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchLots = useCallback(async () => {
    if (!selectedAccountId) return;

    setLoadingLots(true);
    setError(null);
    try {
      const response = await investmentsService.getInvestmentTrades(
        selectedAccountId,
        selectedSymbol || undefined
      );
      const data = response?.data || (response as any);
      let lotsData = data.trades || [];
      
      // Apply sorting
      lotsData = [...lotsData].sort((a: InvestmentTrade, b: InvestmentTrade) => {
        let aValue: any;
        let bValue: any;

        switch (lotsSortField) {
          case 'trade_date':
            aValue = new Date(a.trade_date).getTime();
            bValue = new Date(b.trade_date).getTime();
            break;
          case 'symbol':
            aValue = (a.symbol || '').toLowerCase();
            bValue = (b.symbol || '').toLowerCase();
            break;
          case 'transaction_type':
            aValue = (a.transaction_type || '').toLowerCase();
            bValue = (b.transaction_type || '').toLowerCase();
            break;
          case 'shares':
            aValue = parseFloat(a.shares || '0');
            bValue = parseFloat(b.shares || '0');
            break;
          case 'price':
            aValue = parseFloat(a.price || '0');
            bValue = parseFloat(b.price || '0');
            break;
          case 'amount':
            aValue = tradeAmount(a);
            bValue = tradeAmount(b);
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) return lotsSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return lotsSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
      
      setLots(lotsData);
    } catch (err: any) {
      console.error('Error fetching investment lots:', err);
      let errorMessage = 'Failed to fetch ACB transactions';
      if (err && typeof err === 'object') {
        if (err.message) {
          errorMessage = err.message;
        } else if (err.error) {
          errorMessage = err.error;
        } else if (err.details?.message) {
          errorMessage = err.details.message;
        }
      } else if (typeof err === 'string') {
        errorMessage = err;
      }
      setError(errorMessage);
      setLots([]);
    } finally {
      setLoadingLots(false);
    }
  }, [selectedAccountId, selectedSymbol, lotsSortField, lotsSortDirection]);

  useEffect(() => {
    fetchHoldings();
    fetchLots();
  }, [fetchHoldings, fetchLots]);

  useEffect(() => {
    if (tabValue === 1) {
      fetchLots();
    }
  }, [tabValue, fetchLots]);

  const handleAddTransaction = async () => {
    if (!selectedAccountId || !transactionForm.symbol || !transactionForm.shares || !transactionForm.price) {
      setError('Please fill in all required fields');
      return;
    }

    setAdding(true);
    setError(null);
    try {
  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);


      await investmentsService.createTransaction({
        account_id: selectedAccountId,
        symbol: transactionForm.symbol,
        shares: transactionForm.shares,
        price: transactionForm.price,
        date: transactionForm.date,
        transaction_type: transactionForm.transaction_type,
        currency: selectedAccount?.currency || 'CAD',
        fees: transactionForm.fees || undefined,
        fx_rate: transactionForm.fx_rate || undefined,
        notes: transactionForm.notes || undefined,
      });

      setAddDialogOpen(false);
      setSuccessMessage('Transaction added successfully');
      setTransactionForm({
        symbol: '',
        shares: '',
        price: '',
        date: new Date().toISOString().split('T')[0],
        transaction_type: 'BUY',
        fees: '',
        fx_rate: '',
        notes: '',
      });
      fetchHoldings();
      fetchLots();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error || 'Failed to add transaction';
      setError(errorMessage);
    } finally {
      setAdding(false);
    }
  };

  /**
   * The CAD amount a trade converts to. Fees are ADDED on an acquisition (they
   * are part of the cost base) and SUBTRACTED on a disposition (they reduce
   * proceeds), then the whole thing converts at the rate stored with the trade
   * -- never today's rate.
   */
  const tradeAmount = (t: InvestmentTrade): number => {
    const gross = parseFloat(t.shares || '0') * parseFloat(t.price || '0');
    const fees = parseFloat(t.fees || '0');
    const fx = parseFloat(t.fx_rate || '1') || 1;
    const isDisposal = t.transaction_type === 'SELL' || t.transaction_type === 'TRANSFER_OUT';
    return (isDisposal ? gross - fees : gross + fees) * fx;
  };

  const formatCurrency = (amount: string, currency: string = 'CAD') => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(parseFloat(amount || '0'));
  };

  const selectedAccount = accounts.find((acc) => acc.id === selectedAccountId);

  /**
   * This account's positions, priced, for the chart.
   *
   * Taken from the portfolio endpoint filtered to THIS account, so the share
   * counts are the account's own. Using the pooled ACB figures instead valued
   * a $8,903 account at $11,234 and floored its cash at zero.
   *
   * Falls back to the brokerage snapshot for anything the portfolio endpoint
   * does not carry.
   */
  const accountPositions = React.useMemo(() => {
    const mine = portfolioRows.filter(
      (h: any) => String(h.account_id) === String(selectedAccountId));
    if (mine.length > 0) {
      return mine.map((h: any) => ({
        symbol: String(h.symbol).toUpperCase(),
        value: Number(h.market_value) || Number(h.book_cost) || 0,
      }));
    }
    return institutionHoldings.map((h: any) => ({
      symbol: String(h.symbol).toUpperCase(),
      value: Number(h.value_base) || Number(h.value) || 0,
    }));
  }, [portfolioRows, institutionHoldings, selectedAccountId]);

  /**
   * The date the account's figures are good as of.
   *
   * The newest imported activity, NOT the time of import: an import performed
   * today from a file that ends in July is current to July, and saying
   * otherwise claims a freshness the data does not have.
   */
  const asOfLabel = React.useMemo(() => {
    const dates = (holdings as any[])
      .map((h: any) => h.last_trade_date || h.as_of)
      .concat(institutionHoldings.map((h: any) => h.as_of))
      .filter(Boolean)
      .map((d: any) => new Date(d))
      .filter((d: Date) => !isNaN(d.getTime()));
    if (dates.length === 0) return null;
    const newest = new Date(Math.max(...dates.map((d) => d.getTime())));
    return newest.toLocaleDateString(undefined,
      { year: 'numeric', month: 'short', day: 'numeric' });
  }, [holdings, institutionHoldings]);



  return (
    <MainLayout>
      <Box sx={{ p: 3 }}>
        {/* Above the holdings, because it changes what the numbers below mean.
            Renders nothing unless a ticker genuinely names two companies. */}
        <SymbolReviewBanner />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Investment Holdings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage holdings with ACB tracking
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setAddDialogOpen(true)}
            disabled={!selectedAccountId || accounts.length === 0}
          >
            Add Transaction
          </Button>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <AccountPicker
              accounts={accounts as any}
              selectedId={selectedAccountId}
              onSelect={(id) => {
                setSelectedAccountId(id);
                try { localStorage.setItem(`${selectionKey}:account`, id); } catch { /* private mode */ }
              }}
              scope={scope}
              onScopeChange={(sc) => {
                setScope(sc);
                try { localStorage.setItem(`${selectionKey}:scope`, String(sc)); } catch { /* private mode */ }
              }}
              emptyHint="No investment accounts for this selection."
            />
          </CardContent>
        </Card>

        {selectedAccountId && (
          <Paper sx={{ width: '100%', overflow: 'hidden' }}>
            <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tab label="Holdings (pooled ACB)" />
              <Tab label="Trade History" />
            </Tabs>

            {tabValue === 0 && (
              <>
                {/* The mix renders for EVERY account, not just the ones a
                    broker sends a positions snapshot for.
                    
                    It used to live inside the institution branch, so an
                    account built from an activity import -- which takes the
                    other branch -- showed no chart and no cash at all, while
                    holding both. Cash especially: an account is never "no
                    cash", it is cash as of the last thing we were told. */}
                {!loading && accountPositions.length > 0 && (
                  <Box sx={{ px: 2, pt: 2 }}>
                    <PositionMix
                      positions={accountPositions}
                      accountTotal={(() => {
                        // The recorded balance means different things by
                        // account type, and this endpoint does not say which.
                        //
                        // A broker-synced account's balance already INCLUDES
                        // its positions. An import-built one holds only the
                        // cash, because positions are trades and never ledger
                        // lines -- so subtracting positions from it gave
                        // 375.58 - 15,416.05 and floored cash at zero on an
                        // account with $375.58 in it.
                        //
                        // If the recorded balance already covers the
                        // positions, it includes them; otherwise it is the
                        // cash and the positions sit on top.
                        const recorded = Number((selectedAccount as any)?.current_amount) || 0;
                        const positions = accountPositions.reduce(
                          (sum, p) => sum + (Number(p.value) || 0), 0);
                        return recorded >= positions ? recorded : recorded + positions;
                      })()}
                      currency={selectedAccount?.currency || 'CAD'}
                      baseCurrency={baseCurrency}
                    />
                    {asOfLabel && (
                      <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mt: 0.5 }}>
                        Cash as of {asOfLabel} &mdash; this account is updated by
                        import, so it reflects the newest activity uploaded.
                      </Typography>
                    )}
                  </Box>
                )}
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : institutionHoldings.length > 0 ? (
                  /*
                    No trade history, but the brokerage reports positions.
                    Shown with an explicit caveat rather than dressed up as ACB:
                    a cost basis that has not been replayed from trades cannot
                    account for superficial losses or partial dispositions, and
                    presenting it as if it had would be the more damaging error.
                  */
                  <Box>
                    <Alert severity="info" sx={{ m: 2 }}>
                      Positions reported by the institution. No trade history has been
                      imported for this account, so cost basis is the brokerage's own
                      figure rather than a calculated ACB. Import an activity export to
                      get full ACB with purchase dates.
                    </Alert>
                    {/* Weights include CASH, derived as the account balance
                        less the value of its positions. A pie of positions
                        alone renormalises to 100% and overstates how large the
                        top holding is relative to the actual money. */}

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Symbol</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                            <TableCell align="right">Cost basis</TableCell>
                            <TableCell align="right">Market value</TableCell>
                            <TableCell>Currency</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {institutionHoldings.map((h: any) => (
                            // Clickable like the ACB rows above. These positions
                            // have no imported trades, so the drill-down opens
                            // mostly empty -- which is itself the answer to
                            // "why is there no cost basis here", and is more
                            // useful than a row that ignores the click.
                            <TableRow
                              key={h.symbol}
                              hover
                              tabIndex={0}
                              onClick={() => setLedgerSymbol(h.symbol)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') setLedgerSymbol(h.symbol);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell sx={{ fontWeight: 650 }}>{h.symbol}</TableCell>
                              <TableCell align="right">{formatShares(h.quantity)}</TableCell>
                              <TableCell align="right">
                                {/* The pencil records the book value the BROKER
                                    reports, for cost the trade replay cannot
                                    reach -- return of capital and reinvested
                                    distributions arrive with no amount. */}
                                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                  {h.cost_basis
                                    ? fmtMoney(h.cost_basis, h.currency, { base: baseCurrency })
                                    : '—'}
                                  <BookValueEditor
                                    accountId={selectedAccountId}
                                    symbol={h.symbol}
                                    replayedValue={h.cost_basis ?? 0}
                                    overridden={h.book_value_overridden}
                                    currency={h.currency}
                                    onSaved={fetchHoldings}
                                  />
                                </Box>
                              </TableCell>
                              <TableCell align="right">
                                {/* An institution that reports no market value shows a
                                    dash, not zero. Zero reads as a closed position. */}
                                {Number(h.value) > 0
                                  ? fmtMoney(h.value, h.currency, { base: baseCurrency })
                                  : '—'}
                              </TableCell>
                              <TableCell>{h.currency}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ) : holdings.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No holdings found for this account. Add a transaction, or import an
                      activity export from your brokerage.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={holdingsSortField === 'symbol'}
                              direction={holdingsSortField === 'symbol' ? holdingsSortDirection : 'asc'}
                              onClick={() => {
                                if (holdingsSortField === 'symbol') {
                                  setHoldingsSortDirection(holdingsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setHoldingsSortField('symbol');
                                  setHoldingsSortDirection('asc');
                                }
                              }}
                            >
                              Symbol
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={holdingsSortField === 'total_shares'}
                              direction={holdingsSortField === 'total_shares' ? holdingsSortDirection : 'asc'}
                              onClick={() => {
                                if (holdingsSortField === 'total_shares') {
                                  setHoldingsSortDirection(holdingsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setHoldingsSortField('total_shares');
                                  setHoldingsSortDirection('asc');
                                }
                              }}
                            >
                              Shares
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={holdingsSortField === 'total_cost_basis'}
                              direction={holdingsSortField === 'total_cost_basis' ? holdingsSortDirection : 'asc'}
                              onClick={() => {
                                if (holdingsSortField === 'total_cost_basis') {
                                  setHoldingsSortDirection(holdingsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setHoldingsSortField('total_cost_basis');
                                  setHoldingsSortDirection('asc');
                                }
                              }}
                            >
                              Cost Basis
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={holdingsSortField === 'acb_per_share'}
                              direction={holdingsSortField === 'acb_per_share' ? holdingsSortDirection : 'asc'}
                              onClick={() => {
                                if (holdingsSortField === 'acb_per_share') {
                                  setHoldingsSortDirection(holdingsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setHoldingsSortField('acb_per_share');
                                  setHoldingsSortDirection('asc');
                                }
                              }}
                            >
                              ACB per Share
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">Market Value</TableCell>
                          <TableCell align="right">Unrealised</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {holdings.map((holding) => (
                          <TableRow
                            key={holding.symbol}
                            hover
                            onClick={() => setLedgerSymbol(holding.symbol)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') setLedgerSymbol(holding.symbol);
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Show purchase history for ${holding.symbol}`}
                            sx={{ cursor: 'pointer' }}
                          >
                            <TableCell>
                              {/* The whole row opens the history. A cost basis
                                  with nothing behind it cannot be checked, so
                                  reaching the trades has to be obvious. */}
                              <Typography
                                variant="body1"
                                fontWeight={600}
                                sx={{ textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                              >
                                {holding.symbol}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">{formatShares(holding.total_shares)}</TableCell>
                            <TableCell align="right">
                              {/* The pencil belongs HERE most of all: this table
                                  renders for import-only accounts, which are
                                  exactly the ones whose cost the trade replay
                                  cannot reach. Return of capital and reinvested
                                  distributions arrive with no amount at all. */}
                              <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                                {formatCurrency(holding.total_cost_basis, selectedAccount?.currency)}
                                <BookValueEditor
                                  accountId={selectedAccountId}
                                  symbol={holding.symbol}
                                  replayedValue={holding.total_cost_basis ?? 0}
                                  overridden={(holding as any).book_value_overridden}
                                  currency={selectedAccount?.currency || 'CAD'}
                                  onSaved={fetchHoldings}
                                />
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(holding.acb_per_share, selectedAccount?.currency)}
                            </TableCell>
                            <TableCell align="right">
                              {/* Market value, NOT cost basis. This column
                                  used to render total_cost_basis, making it a
                                  silent duplicate of the column beside it --
                                  so a position that had doubled looked flat. */}
                              <Typography variant="body1" fontWeight={600}>
                                {(() => {
                                  const q = quotes[String(holding.symbol).toUpperCase()];
                                  if (!q) return '—';
                                  const shares = parseFloat(holding.total_shares || '0');
                                  return formatCurrency(String(q.price * shares), selectedAccount?.currency);
                                })()}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {(() => {
                                const q = quotes[String(holding.symbol).toUpperCase()];
                                if (!q) return (
                                  <Tooltip title="No live quote available, so the gain cannot be computed. The cost basis is still exact.">
                                    <Typography variant="body2" color="text.disabled">—</Typography>
                                  </Tooltip>
                                );
                                const shares = parseFloat(holding.total_shares || '0');
                                const cost = parseFloat(holding.total_cost_basis || '0');
                                const gain = q.price * shares - cost;
                                return (
                                  <Typography
                                    variant="body2"
                                    fontWeight={650}
                                    color={gain >= 0 ? 'success.main' : 'error.main'}
                                  >
                                    {gain >= 0 ? '+' : ''}
                                    {formatCurrency(String(gain), selectedAccount?.currency)}
                                  </Typography>
                                );
                              })()}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {tabValue === 1 && (
              <>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Symbol</InputLabel>
                    <Select
                      value={selectedSymbol}
                      label="Filter by Symbol"
                      onChange={(e) => setSelectedSymbol(e.target.value)}
                    >
                      <MenuItem value="">All Symbols</MenuItem>
                      {holdings.map((holding) => (
                        <MenuItem key={holding.symbol} value={holding.symbol}>
                          {holding.symbol}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                {loadingLots ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : lots.length === 0 ? (
                  <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No trades found{selectedSymbol ? ` for ${selectedSymbol}` : ''}. Add a transaction to get started.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TableSortLabel
                              active={lotsSortField === 'trade_date'}
                              direction={lotsSortField === 'trade_date' ? lotsSortDirection : 'asc'}
                              onClick={() => {
                                if (lotsSortField === 'trade_date') {
                                  setLotsSortDirection(lotsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setLotsSortField('trade_date');
                                  setLotsSortDirection('desc');
                                }
                              }}
                            >
                              Date
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={lotsSortField === 'symbol'}
                              direction={lotsSortField === 'symbol' ? lotsSortDirection : 'asc'}
                              onClick={() => {
                                if (lotsSortField === 'symbol') {
                                  setLotsSortDirection(lotsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setLotsSortField('symbol');
                                  setLotsSortDirection('asc');
                                }
                              }}
                            >
                              Symbol
                            </TableSortLabel>
                          </TableCell>
                          <TableCell>
                            <TableSortLabel
                              active={lotsSortField === 'transaction_type'}
                              direction={lotsSortField === 'transaction_type' ? lotsSortDirection : 'asc'}
                              onClick={() => {
                                if (lotsSortField === 'transaction_type') {
                                  setLotsSortDirection(lotsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setLotsSortField('transaction_type');
                                  setLotsSortDirection('asc');
                                }
                              }}
                            >
                              Type
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={lotsSortField === 'shares'}
                              direction={lotsSortField === 'shares' ? lotsSortDirection : 'asc'}
                              onClick={() => {
                                if (lotsSortField === 'shares') {
                                  setLotsSortDirection(lotsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setLotsSortField('shares');
                                  setLotsSortDirection('asc');
                                }
                              }}
                            >
                              Shares
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={lotsSortField === 'price'}
                              direction={lotsSortField === 'price' ? lotsSortDirection : 'asc'}
                              onClick={() => {
                                if (lotsSortField === 'price') {
                                  setLotsSortDirection(lotsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setLotsSortField('price');
                                  setLotsSortDirection('asc');
                                }
                              }}
                            >
                              Price per Share
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">
                            <TableSortLabel
                              active={lotsSortField === 'amount'}
                              direction={lotsSortField === 'amount' ? lotsSortDirection : 'asc'}
                              onClick={() => {
                                if (lotsSortField === 'amount') {
                                  setLotsSortDirection(lotsSortDirection === 'asc' ? 'desc' : 'asc');
                                } else {
                                  setLotsSortField('amount');
                                  setLotsSortDirection('asc');
                                }
                              }}
                            >
                              Amount (CAD)
                            </TableSortLabel>
                          </TableCell>
                          <TableCell align="right">Fees</TableCell>
                          <TableCell align="right">FX rate</TableCell>
                          <TableCell>Notes</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lots.map((lot) => (
                          <TableRow key={lot.id}>
                            <TableCell>
                              {new Date(lot.trade_date).toLocaleDateString()}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {lot.symbol}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: lot.transaction_type === 'BUY' ? 'success.main' : 'error.main',
                                  fontWeight: 600,
                                }}
                              >
                                {lot.transaction_type}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              {parseFloat(lot.shares).toFixed(6)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(lot.price, lot.currency)}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(String(tradeAmount(lot)), 'CAD')}
                            </TableCell>
                            <TableCell align="right">
                              {formatCurrency(lot.fees, lot.currency)}
                            </TableCell>
                            <TableCell align="right">
                              {parseFloat(lot.fx_rate || '1').toFixed(4)}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" color="text.secondary">
                                {lot.notes || '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Paper>
        )}
      </Box>

      {/* Add Transaction Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Investment Transaction</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Symbol"
              value={transactionForm.symbol}
              onChange={(e) =>
                setTransactionForm({ ...transactionForm, symbol: e.target.value.toUpperCase() })
              }
              fullWidth
              required
              placeholder="e.g., AAPL, TSLA"
            />
            <FormControl fullWidth>
              <InputLabel>Transaction Type</InputLabel>
              <Select
                value={transactionForm.transaction_type}
                label="Transaction Type"
                onChange={(e) =>
                  setTransactionForm({
                    ...transactionForm,
                    transaction_type: e.target.value as 'BUY' | 'SELL',
                  })
                }
              >
                <MenuItem value="BUY">Buy</MenuItem>
                <MenuItem value="SELL">Sell</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Shares"
              type="number"
              value={transactionForm.shares}
              onChange={(e) => setTransactionForm({ ...transactionForm, shares: e.target.value })}
              fullWidth
              required
              inputProps={{ step: '0.000001', min: '0.000001' }}
            />
            <TextField
              label="Price per Share"
              type="number"
              value={transactionForm.price}
              onChange={(e) => setTransactionForm({ ...transactionForm, price: e.target.value })}
              fullWidth
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">{selectedAccount?.currency || 'CAD'}</InputAdornment>,
              }}
              inputProps={{ step: '0.01', min: '0.01' }}
            />
            <TextField
              label="Commission / fees"
              type="number"
              value={transactionForm.fees}
              onChange={(e) => setTransactionForm({ ...transactionForm, fees: e.target.value })}
              fullWidth
              helperText="Added to the cost base on a buy, deducted from proceeds on a sell"
              InputProps={{
                startAdornment: <InputAdornment position="start">{selectedAccount?.currency || 'CAD'}</InputAdornment>,
              }}
              inputProps={{ step: '0.01', min: '0' }}
            />
            {(selectedAccount?.currency || 'CAD') !== 'CAD' && (
              <TextField
                label="Exchange rate to CAD on the trade date"
                type="number"
                value={transactionForm.fx_rate}
                onChange={(e) => setTransactionForm({ ...transactionForm, fx_rate: e.target.value })}
                fullWidth
                required
                helperText="CRA requires the rate on the TRANSACTION date, not today's. It is stored with the trade so this entry never re-rates."
                inputProps={{ step: '0.00000001', min: '0' }}
              />
            )}
            <TextField
              label="Date"
              type="date"
              value={transactionForm.date}
              onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Notes (Optional)"
              value={transactionForm.notes}
              onChange={(e) => setTransactionForm({ ...transactionForm, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddTransaction}
            variant="contained"
            disabled={adding || !transactionForm.symbol || !transactionForm.shares || !transactionForm.price}
          >
            {adding ? <CircularProgress size={20} /> : 'Add Transaction'}
          </Button>
        </DialogActions>
      </Dialog>
      <SymbolLedgerDialog
        open={!!ledgerSymbol}
        onClose={() => setLedgerSymbol(null)}
        accountId={selectedAccountId}
        symbol={ledgerSymbol ?? ''}
      />
    </MainLayout>
  );
};

